import { createHash } from 'node:crypto';
import type { ActiveAbility } from './types.js';
import { buildPartsIndex, type PartRef } from './parts.js';

/** Who minted a pressing — the write-permission gate for the P5 learner. */
export type PressingOrigin = 'system' | 'marketplace' | 'user' | 'agent';

export interface ManifestV2 {
  schema: 'vestra.ability/v2';
  slug: string;
  name: string;
  version: string;
  description: string;
  scope: string;
  parts: PartRef[];
  origin: PressingOrigin;
  source: { type: string; url?: string; ref?: string; subpath?: string } | null;
}

export function originFromScope(scope: string): PressingOrigin {
  if (scope === 'system') return 'system';
  if (scope === 'marketplace') return 'marketplace';
  return 'user';
}

/** Deterministic JSON: object keys sorted at every depth, arrays kept in order. */
export function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, v]) => v !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
}

export function buildManifestV2(
  active: ActiveAbility,
  opts?: { origin?: PressingOrigin; source?: ManifestV2['source'] },
): ManifestV2 {
  const a = active.ability;
  return {
    schema: 'vestra.ability/v2',
    slug: a.slug,
    name: a.name,
    version: a.version,
    description: a.description ?? '',
    scope: a.scope,
    parts: buildPartsIndex(active),
    origin: opts?.origin ?? originFromScope(a.scope),
    source: opts?.source ?? null,
  };
}

/** The pressing identity: sha256 over the manifest's canonical serialization. */
export function computePressingHash(manifest: ManifestV2): string {
  return `sha256:${createHash('sha256').update(stableStringify(manifest)).digest('hex')}`;
}
